import React, { useState, useEffect, useRef } from 'react';
import classes from './calendar.module.css';

const Calendar = (props) => {
    const [selected, setSelected] = useState([]);
    const [selectedDates, setSelectedDates] = useState([]);
    const [month, setMonth] = useState(new Date().getMonth());
    const [year, setYear] = useState(new Date().getFullYear());
    const calendarRef = useRef(null);
    const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    // Close the calendar when clicking outside
    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                props.onClose();
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [props]);

    const getDaysInMonth = (month, year) => {
        return new Date(year, month + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (month, year) => {
        return new Date(year, month, 1).getDay();
    };

    // Get today's date for comparison
    const today = new Date();
    const currentDate = today.setHours(0, 0, 0, 0); // Set time to midnight for date comparison

    const formatDate = (day) => {
        // Create a Date object using the selected day, month, and year
        const date = new Date(year, month, day);
        // Format the date as "YYYY-MM-DD"
        const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        return formattedDate;
    };

    const handleDateClick = (day) => {
        if (props.isMultiple) {
            // Allow multiple dates to be selected
            if (selectedDates.includes(day)) {
                setSelectedDates(selectedDates.filter(date => date !== day));
            } else {
                setSelectedDates([...selectedDates, day]);
            }
        } else {
            // Single date selection
            setSelectedDates([day]); // Replace the existing selection with the new date
        }
    };
    const handleClear = () => {
        setSelectedDates([]);
        setSelected([]);
    };
    const handleCancel = () => {
        props.onClose();
        setSelectedDates([]);
        setSelected([]);
    };
    const handleDone = () => {
        const formattedDates = selectedDates.map(day => formatDate(day)); // <-- Added formatting step
        props.handleDateSelect(formattedDates); // <-- Updated to pass formatted dates
    };
    const handlePreviousMonth = () => {
        if (month === 0) {
            setMonth(11);
            setYear(year - 1);
        } else {
            setMonth(month - 1);
        }
    };

    const handleNextMonth = () => {
        if (month === 11) {
            setMonth(0);
            setYear(year + 1);
        } else {
            setMonth(month + 1);
        }
    };

    const handlePreviousYear = () => {
        setYear(year - 1);
    };

    const handleNextYear = () => {
        setYear(year + 1);
    };

    const renderDays = () => {
        const daysInMonth = getDaysInMonth(month, year);
        const firstDay = getFirstDayOfMonth(month, year);
        const days = [];

        const availableDates = props.availableDates?.map(slot => slot.date) || null;     
        // Calculate how many days to show from the previous month
        const prevMonthDaysCount = firstDay === 0 ? 6 : firstDay - 1;
        const prevMonth = month === 0 ? 11 : month - 1;
        const prevMonthYear = month === 0 ? year - 1 : year;
        const daysInPrevMonth = getDaysInMonth(prevMonth, prevMonthYear);

        // Add previous month's days
        for (let i = prevMonthDaysCount; i > 0; i--) {
            days.push(
                <div
                    key={`prev-${i}`}
                    className={`${classes.CalendarDate} ${classes.CalendarDateGrey}`}
                >
                    <span>{daysInPrevMonth - i + 1}</span>
                </div>
            );
        }

        // Add days of the current month
        for (let day = 1; day <= daysInMonth; day++) {
            const dayDate = new Date(year, month, day).setHours(0, 0, 0, 0); // Compare with current date
            const formattedDate = formatDate(day); // Format the date as "YYYY-MM-DD"
            const isAvailable = availableDates ? availableDates.includes(formattedDate) : true;
            const isPastDate = dayDate < currentDate;
            const isFutureDate = dayDate > currentDate;
            const isSelected = selectedDates.includes(day);
            
            // Special case for Date of Birth field from Legal Info component
            // props.fieldName will be 'dateOfBirth' for the DOB field
            let isDisabled;
            if (props.fieldName === 'dateOfBirth') {
                // For Date of Birth, ONLY future dates should be disabled
                isDisabled = isFutureDate;
            } else {
                // Normal behavior for all other date fields
                isDisabled = props.availableDates ? !isAvailable : isPastDate;
            }
            
            days.push(
                <div
                    key={day}
                    className={`${classes.CalendarDate} ${isDisabled ? classes.CalendarDateGrey : ''} 
                    ${isSelected ? classes.CalendarDateSelected : ''}`}
                    onClick={() => (!isDisabled) && handleDateClick(day)}
                >
                    <span>{day}</span>
                </div>
            );
        }

        // Calculate how many days to show from the next month to complete the week
        const totalDays = days.length;
        const nextMonthDaysCount = totalDays % 7 === 0 ? 0 : 7 - (totalDays % 7);

        // Add next month's days
        for (let i = 1; i <= nextMonthDaysCount; i++) {
            days.push(
                <div
                    key={`next-${i}`}
                    className={`${classes.CalendarDate} ${classes.CalendarDateGrey}`}
                >
                    <span>{i}</span>
                </div>
            );
        }

        return days;
    };
    return (
        <div className={[classes.Calendar, props.method && classes.CalendarWithIncreaseHeight].join(" ")} 
        onClick={(e) => e.preventDefault()} ref={calendarRef}>
            {props.method && <>
                <div className={classes.CalendarSelection}>Date Selection Method</div>
                <div className={classes.CalendarMethod}>
                    <input type="radio" name="method" id="1" value="1" />
                    <label htmlFor="1">Multiple</label>
                    <input type="radio" name="method" id="2" value="2" />
                    <label htmlFor="2">Range</label>
                </div>
                <div className={classes.Border} />
            </>}
            <div className={classes.CalendarMonthYearContainer}>
                <div className={classes.CalendarOpts}>
                    <button onClick={handlePreviousMonth}>‹</button>
                    <span className={classes.CalendarMonthYear}>
                        {new Date(year, month).toLocaleString('default', { month: 'long' })}
                    </span>
                    <button onClick={handleNextMonth}>›</button>
                </div>
                <div className={classes.CalendarYearNavigation}>
                    <button onClick={handlePreviousYear}>‹</button>
                    <span className={classes.CalendarMonthYear}>{year}</span>
                    <button onClick={handleNextYear}>›</button>
                </div>
            </div>
            <div className={classes.CalendarDays}>
                {daysOfWeek.map((day) => (
                    <div key={day} className={classes.CalendarDay}>{day}</div>
                ))}
            </div>
            <div className={classes.CalendarDates}>
                {renderDays()}
            </div>
            <div className={classes.CalendarButtons}>
                <button className={classes.CalendarButton} onClick={handleCancel}>Cancel</button>
                <button className={classes.CalendarButton} onClick={handleClear}>Clear</button>
                <button className={classes.CalendarButton} onClick={handleDone}>Done</button>
            </div>
        </div>
    );
};

export default Calendar;
